<template>
  <button
    class="button"
    :class="[
      `type-${type}`,
      `color-type-${colorType}`,
      `size-${size}`,
      buttonClass,
    ]"
    :disabled="disabled"
    :style="buttonStyle"
    @click="handleClick"
  >
    <div class="button-content">
      <vs-icon
        v-if="icon"
        :icon="icon"
        class="button-icon material-icons-outlined"
      />
      <span v-if="label" class="button-label">{{ label }}</span>
    </div>
  </button>
</template>

<script>
import { ColorMixin } from "@/mixins";

export default {
  name: "Button",
  mixins: [ColorMixin],
  props: {
    /**
     * アイコン名
     * @type {String}
     * @optional
     */
    icon: {
      type: String,
      default: "",
    },
    /**
     * ラベルテキスト
     * @type {String}
     * @optional
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * ボタンのタイプ（filled, border, flat）
     * @type {String}
     * @optional
     */
    type: {
      type: String,
      default: "border",
    },
    /**
     * ボタンのカラータイプ（normal, cancel, danger, secondary）
     * @type {String}
     * @optional
     */
    colorType: {
      type: String,
      default: "normal",
    },
    /**
     * ボタンのサイズ（sm, md）
     * @type {String}
     * @optional
     */
    size: {
      type: String,
      default: "md",
    },
    /**
     * 無効化されているかどうか
     * @type {Boolean}
     * @optional
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * ボタンのスタイル
     * @type {Object|String}
     * @optional
     */
    buttonStyle: {
      type: [Object, String],
      default: () => ({}),
    },
    /**
     * ボタンのクラス
     * @type {String}
     * @optional
     */
    buttonClass: {
      type: String,
      default: "",
    },
  },
  methods: {
    handleClick(event) {
      if (!this.disabled) {
        this.$emit("click", event);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid;
  background-color: transparent;

  // size-md (デフォルト)
  &.size-md {
    padding: 0.5rem 2rem;
    min-height: 2.7rem;
  }

  // size-sm
  &.size-sm {
    padding: 0.5rem;
  }

  &:disabled {
    cursor: not-allowed;
  }

  .button-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .button-icon {
    font-size: 18px;
  }

  .button-label {
    white-space: nowrap;
  }

  // border type
  &.type-border {
    border: 1px solid;

    &.color-type-normal {
      border-color: $aisea-blue;
      color: $aisea-blue;
      background-color: transparent;

      &:hover:not(:disabled) {
        background-color: $aisea-blue;
        color: white;
      }

      &:disabled {
        background-color: #f5f5f5;
        border-color: #dcdcdc;
        color: #bdbdbd;
      }
    }

    &.color-type-cancel {
      border-color: #e0e0e0;
      background-color: #f6f6f6;
      color: #333333;

      &:hover:not(:disabled) {
        background-color: #e8e8e8;
        border-color: #d0d0d0;
      }
    }

    &.color-type-danger {
      border-color: #e74c3c;
      color: #e74c3c;
      background-color: transparent;

      &:hover:not(:disabled) {
        background-color: #e74c3c;
        color: white;
      }
    }

    &.color-type-secondary {
      border-color: #9e9e9e;
      color: #9e9e9e;
      background-color: transparent;

      &:hover:not(:disabled) {
        background-color: #9e9e9e;
        color: white;
      }
    }
  }

  // filled type
  &.type-filled {
    border: none;

    &.color-type-normal {
      background-color: $aisea-blue;
      color: white;

      &:hover:not(:disabled) {
        background-color: darken($aisea-blue, 10%);
      }

      &:disabled {
        background-color: #dcdcdc;
        color: #bdbdbd;
      }
    }

    &.color-type-cancel {
      background-color: #f6f6f6;
      color: #333333;

      &:hover:not(:disabled) {
        background-color: #e8e8e8;
      }

      &:disabled {
        background-color: #f5f5f5;
        color: #bdbdbd;
      }
    }

    &.color-type-danger {
      background-color: #e74c3c;
      color: white;

      &:hover:not(:disabled) {
        background-color: darken(#e74c3c, 10%);
      }

      &:disabled {
        background-color: #dcdcdc;
        color: #bdbdbd;
      }
    }

    &.color-type-secondary {
      background-color: #9e9e9e;
      color: white;

      &:hover:not(:disabled) {
        background-color: darken(#9e9e9e, 10%);
      }

      &:disabled {
        background-color: #dcdcdc;
        color: #bdbdbd;
      }
    }
  }

  // flat type
  &.type-flat {
    border: none;

    &.color-type-normal {
      background-color: transparent;
      color: $aisea-blue;

      &:hover:not(:disabled) {
        background-color: rgba($aisea-blue, 0.1);
      }

      &:disabled {
        color: #bdbdbd;
      }
    }

    &.color-type-cancel {
      background-color: transparent;
      color: #333333;

      &:hover:not(:disabled) {
        background-color: rgba(0, 0, 0, 0.05);
      }

      &:disabled {
        color: #bdbdbd;
      }
    }

    &.color-type-danger {
      background-color: transparent;
      color: #e74c3c;

      &:hover:not(:disabled) {
        background-color: rgba(231, 76, 60, 0.1);
      }

      &:disabled {
        color: #bdbdbd;
      }
    }

    &.color-type-secondary {
      background-color: transparent;
      color: #9e9e9e;

      &:hover:not(:disabled) {
        background-color: rgba(158, 158, 158, 0.1);
      }

      &:disabled {
        color: #bdbdbd;
      }
    }
  }
}
</style>
